import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db, auth } from './firebase';
import Upgrade from './Upgrade';
import {
  PlayIcon,
  ClockIcon,
  StarIcon,
  AcademicCapIcon,
  ChartBarIcon,
  VideoCameraIcon,
  FireIcon,
  ShieldCheckIcon,
  ListBulletIcon
} from '@heroicons/react/24/outline';

const Feed = ({ userData }) => {
  const [dbVideos, setDbVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const isPaid = userData?.isPaid || auth.currentUser?.email === process.env.REACT_APP_ADMIN_EMAIL;

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'videos'));
        const videosList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const sortedVideos = videosList.sort((a, b) => {
          const numA = parseInt(a.title?.match(/\d+/)?.[0] || 0);
          const numB = parseInt(b.title?.match(/\d+/)?.[0] || 0);
          return numA - numB;
        });

        setDbVideos(sortedVideos);
      } catch (err) {
        console.error("Erro ao buscar vídeos do Firebase:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  const stats = [
    { name: 'Nível Atual', value: 'Intermediário', icon: AcademicCapIcon, color: 'text-emerald-400' },
    { name: 'Módulos Concluídos', value: `${dbVideos.length > 0 ? 'Iniciando' : '0/45'}`, icon: StarIcon, color: 'text-purple-400' },
    { name: 'Aulas Disponíveis', value: dbVideos.length.toString(), icon: VideoCameraIcon, color: 'text-orange-400' },
    { name: 'Progresso Geral', value: isPaid ? '12%' : 'Bloqueado', icon: ChartBarIcon, color: 'text-blue-400' },
  ];

  const DEFAULT_IMAGE = '/hero_logo.png';

  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/?|.*?[?&]v=))([^"&?/\s]{11})/);
    return match ? match[1] : null;
  };

  // Se NÃO for pago, exibe o Paywall (Upgrade)
  if (!loading && !isPaid) {
    return <Upgrade />;
  }

  return (
    <div className="space-y-10 pb-10">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 animate-fade-in">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Cronograma de Aulas 📝</h1>
          <p className="text-gray-400">Siga a ordem cronológica para um melhor aprendizado.</p>
        </div>
        <div className="bg-emerald-500/10 border border-emerald-500/20 px-6 py-3 rounded-2xl flex items-center gap-3">
          <ListBulletIcon className="w-6 h-6 text-emerald-500" />
          <div>
            <p className="text-[10px] font-bold text-emerald-500/70 uppercase">Organização</p>
            <p className="text-lg font-bold">Ordem Numérica</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 animate-fade-in" style={{ animationDelay: '0.1s' }}>
        {stats.map((stat, idx) => (
          <div key={idx} className="bg-white/5 border border-white/10 p-6 rounded-[2rem] hover:bg-white/[0.08] transition-all group">
            <div className="flex items-center gap-4">
              <div className={`p-3 bg-white/5 rounded-2xl ${stat.color} group-hover:scale-110 transition-transform`}>
                <stat.icon className="w-6 h-6" />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">{stat.name}</p>
                <p className="text-xl font-black text-white italic">{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-6">
        <div className="flex items-center justify-between px-2">
          <h2 className="text-xl font-black text-white italic uppercase tracking-tighter">Série Original: Formação Trader</h2>
          <div className="flex items-center gap-2 text-xs font-bold text-gray-500">
            <FireIcon className="w-4 h-4 text-orange-500" />
            {dbVideos.length} Aulas Ativas
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {dbVideos.map((video, idx) => {
            const videoId = getYouTubeVideoId(video.youtubeLink);
            const videoThumb = videoId
              ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
              : DEFAULT_IMAGE;

            return (
              <div
                key={video.id}
                onClick={() => navigate(`/dashboard/video/${video.id}`)}
                className="group bg-white/5 border border-white/10 rounded-[2.5rem] overflow-hidden hover:bg-white/[0.08] hover:border-emerald-500/30 transition-all cursor-pointer relative"
              >
                <div className="aspect-video relative overflow-hidden">
                  <img
                    src={videoThumb}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 opacity-80 group-hover:opacity-100"
                    alt={video.title}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-transparent to-transparent opacity-60"></div>

                  <div className="absolute top-6 left-6 flex items-center gap-2 bg-black/60 backdrop-blur-md px-4 py-2 rounded-2xl border border-white/10">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                    <span className="text-[10px] font-black text-white italic uppercase tracking-widest">Aula {idx + 1}</span>
                  </div>

                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center shadow-2xl shadow-emerald-500/50 scale-75 group-hover:scale-100 transition-transform">
                      <PlayIcon className="w-8 h-8 text-white fill-current translate-x-0.5" />
                    </div>
                  </div>
                </div>

                <div className="p-8">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 text-emerald-500">
                      <ShieldCheckIcon className="w-4 h-4" />
                      <span className="text-[10px] font-black uppercase tracking-widest">Conteúdo VIP</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-gray-500">
                      <ClockIcon className="w-3.5 h-3.5" />
                      <span className="text-[10px] font-bold">12:45</span>
                    </div>
                  </div>
                  <h3 className="text-xl font-black text-white italic uppercase leading-none group-hover:text-emerald-400 transition-colors">{video.title}</h3>
                </div>
              </div>
            );
          })}


          {dbVideos.length === 0 && (
            <div className="col-span-full py-20 text-center bg-white/5 border-2 border-dashed border-white/10 rounded-[3rem]">
              <AcademicCapIcon className="w-12 h-12 text-gray-700 mx-auto mb-4" />
              <p className="text-gray-500 font-bold uppercase tracking-widest italic">Aguardando lançamento das aulas pela mesa proprietária...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Feed;
